import {
    CForm,
    CFormInput,
    CFormLabel,
    CFormSelect,
    CFormTextarea,
    CInputGroup,
} from "@coreui/react";
import { ChangeEvent, useEffect, useState } from "react";
import { t } from 'i18next';
import { useLocation, useParams } from "react-router-dom";
import { Button } from "react-bootstrap";
import { useNavigate } from 'react-router';
import { useSpinner } from '@webkit/components/typescript/SpinnerContext';
import { CardLinkModel } from "@models/card-link.model";
import { getToastStore } from "@utils/ToastStore";
import cardLinkService from "@services/cardLink.service";
import { base64ToFile, errorToast, handleDefaultImgClick, isValidUrl, successToast } from "@webkit/functions/utils";
import ImageCarousel from "@webkit/components/typescript/DefaultImagesCarousel";

export default function EditCardLinkPage() {
    const ctxToast = getToastStore();
    const location = useLocation();
    const formData = new FormData();
    const { showSpinner, hideSpinner } = useSpinner();
    const navigate = useNavigate();
    const { cardId } = useParams<{ cardId: string }>(); // /edit/:cardLinkId
    const [title, setTitle] = useState('');
    /* const [minPrice, setMinPrice] = useState('');
    const [maxPrice, setMaxPrice] = useState('');
    const [currency, setCurrency] = useState('Euro'); */
    // const [linkProtocol, setLinkProtocol] = useState('https://');
    const [link, setLink] = useState('');
    const [recommended, setRecommended] = useState(false);

    // gestione immagine
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [preview, setPreview] = useState<string | null>(null);

    // gestione distanza
    const [distanceValue, setDistanceValue] = useState('');
    const [distanceUnit, setDistanceUnit] = useState('km');

    const [details, setDetails] = useState('');
    const [fullAddress, setFullAddress] = useState('');

    const isDisabled = (
        !title ||
        !link
    );

    // --- CARICAMENTO DATI DAL BACKEND ---
    useEffect(() => {
        const fetchCardLink = async () => {
            try {
                showSpinner();
                const segments = location.pathname.split('/').filter(Boolean);
                const IdParent = segments.at(2) ?? '';
                const data = await cardLinkService.findCardLinkById(cardId || '');

                setTitle(data.title);
                setDetails(data.details);
                setFullAddress(data.fullAddress);
                //setCurrency(data.currency);
                //setMinPrice(String(data.minPrice));
                //setMaxPrice(String(data.maxPrice));
                setRecommended(Boolean(data.cardLink.recommended));

                const rawDistance = data.distance?.trim() ?? '';
                // match: es. "2,9 km" → ["2,9 km", "2,9", "km"]
                const match = rawDistance.match(/^(\d+(?:[.,]\d+)?)[\s]*([a-zA-Z]+)$/);

                if (match) {
                    // sostituisci eventuale virgola con punto
                    const numeric = match[1].replace(',', '.');
                    setDistanceValue(numeric);
                    setDistanceUnit(match[2]);
                } else {
                    // fallback: in caso di formato non riconosciuto
                    console.warn('Formato distanza non riconosciuto:', data.distance);
                    setDistanceValue('');
                    setDistanceUnit('km');
                }

                // link completo (es. https://example.com)
                setLink(data.cardLink.link || '');

                if (data.cardLink.photo) {
                    const base64String = data.cardLink.photo;
                    const dataUrl = `data:image/jpeg;base64,${base64String}`;
                    setPreview(dataUrl);

                    //converto il base64 in file
                    const fileFromBase64 = base64ToFile(dataUrl, 'image_from_backend.jpg');
                    //aggiorno anche lo stato selectedFile (utile per eventuale anteprima o debug)
                    setSelectedFile(fileFromBase64);
                }

            } catch (error) {
                console.error('Errore nel caricamento CardLink:', error);
            } finally {
                hideSpinner();
            }
        };
        if (cardId) fetchCardLink();
    }, [cardId, location]);

    // --- CAMBIO FILE ---
    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        const maxSize = 5 * 1024 * 1024;

        if (file) {
            if (!["image/jpeg", "image/png", "image/jpg"].includes(file.type)) {
                errorToast(ctxToast, t("error.invalid_image_format"))
                e.target.value = "";
                return;
            }
            if (file.size > maxSize) {
                errorToast(ctxToast, t("error.too_large"))
                e.target.value = "";
                return;
            }
            setSelectedFile(file);

            const previewUrl = URL.createObjectURL(file);
            setPreview(previewUrl);
        }
    };

    // --- SALVATAGGIO MODIFICHE ---
    const handleUpdate = async () => {
        const isInteger: any = (value: string) => /^[0-9]+$/.test(value);
        /* if(!!minPrice || !!maxPrice){
            if (!isInteger(minPrice) || !isInteger(maxPrice)) {
                alert(t('error.invalid_format_min_max_price'));
                return;
            }
            if (parseInt(minPrice) > parseInt(maxPrice)) {
                alert(t('error.min_price_greater_than_max'));
                return;
            }
        } */

        if (!!distanceValue && isNaN(Number(distanceValue)) && Number(distanceValue) < 0) {
            errorToast(ctxToast, t('validation.invalid_distance'));
            return;
        }

        if (!isValidUrl(link)) {
            errorToast(ctxToast,t('error.invalid_link'));
            return;
        }

        try {
            showSpinner();
            const segments = location.pathname.split('/').filter(Boolean);
            const IdParent = segments.at(1) ?? '';
            let distanceCombined = "";
            if (!!distanceValue) {
                distanceCombined = `${distanceValue} ${distanceUnit}`;
            }

            const model = new CardLinkModel(
                title,
                distanceCombined,
                link, recommended, details, fullAddress);

            if (selectedFile) {
                formData.append('img', selectedFile);
            }

            await cardLinkService.updateCardLink(IdParent, cardId || '', formData, model);
            successToast(ctxToast, t("success.updated"));
            navigate(-1);
        } catch (error: any) {
            console.error('Errore nell\'aggiornamento della CardLink:', error);
        } finally {
            hideSpinner();
        }
    };

    return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <div className="card shadow-lg" style={{ width: '60%', borderRadius: '25px' }}>
                <div
                    className="hst-card-header card-header text-uppercase d-flex align-items-center"
                    style={{ justifyContent: 'center', position: 'relative' }}
                >
                    <div>{t('title.edit_cardlink')}</div>

                    <div
                        className="d-flex align-items-center"
                        style={{
                            position: 'absolute',
                            right: '2rem',
                            top: '50%',
                            transform: 'translateY(-50%)'
                        }}
                    >
                        <CFormLabel className="me-1 mb-0">{t('label.recommended')}</CFormLabel>
                        <span
                            onClick={() => setRecommended(!recommended)}
                            style={{
                                cursor: 'pointer',
                                fontSize: '1.8rem',
                                color: recommended ? '#FFD700' : '#ccc',
                                transition: 'color 0.3s ease',
                            }}
                        >
                            ★
                        </span>
                    </div>
                </div>

                <div className="card-body">
                    <CForm className="p-5">
                        <CFormInput
                            type="text"
                            label={`${t('label.icon_name')} ${t('placeholder.obligatory')}`}
                            className="mb-3"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                        />

                        <div className="mb-3">
                            <CFormLabel htmlFor="distance">{t('label.distance')}</CFormLabel>
                            <CInputGroup id="distance">
                                <CFormInput
                                    className="hst-input-distance-cardlink"
                                    type="number"
                                    placeholder="0"
                                    value={distanceValue}
                                    min="0"
                                    step="1"
                                    onChange={(e) => setDistanceValue(e.target.value)}
                                />
                                <CFormSelect
                                    className="hst-select-distance"
                                    value={distanceUnit}
                                    onChange={(e) => setDistanceUnit(e.target.value)}
                                    style={{ maxWidth: '120px' }}
                                >
                                    <option value="km">{t('label.km')}</option>
                                    <option value={t('label.mi')}>{t('label.mi')}</option>
                                    <option value={t('label.minutes')}>{t('label.minutes')}</option>
                                </CFormSelect>
                            </CInputGroup>
                        </div>

                        {/* <CRow>
                            <CCol xs={1} />
                            <CCol xs={3}>
                                <CFormInput
                                    type="number"
                                    label={`${t('label.minPrice')} *`}
                                    className="mb-3"
                                    min="0"
                                    step="1"
                                    value={minPrice}
                                    onChange={(e) => setMinPrice(e.target.value)}
                                />
                            </CCol>

                            <CCol xs={3}>
                                <CFormInput
                                    type="number"
                                    label={`${t('label.maxPrice')} *`}
                                    className="mb-3"
                                    min="0"
                                    step="1"
                                    value={maxPrice}
                                    onChange={(e) => setMaxPrice(e.target.value)}
                                />
                            </CCol>

                            <CCol xs={4}>
                                <CFormSelect
                                    label={`${t('label.currency')} *`}
                                    className="mb-3"
                                    value={currency}
                                    onChange={(e) => setCurrency(e.target.value)}
                                >
                                    {Currencies.map(({ code, symbol, label }) => (
                                        <option key={code} value={label}>
                                            {`${code} (${symbol}) - ${label}`}
                                        </option>
                                    ))}
                                </CFormSelect>
                            </CCol>
                            <CCol xs={1} />
                        </CRow> */}

                        <CFormLabel htmlFor="link">{t('label.insert_link')} {t('placeholder.obligatory')}</CFormLabel>
                        <CInputGroup className="mb-3">
                            {/* <CFormSelect
                                className="hst-select-link"
                                value={linkProtocol}
                                onChange={(e) => setLinkProtocol(e.target.value)}
                                style={{ maxWidth: '120px' }}
                            >
                                <option value="https://">https://</option>
                                <option value="http://">http://</option>
                            </CFormSelect> */}
                            <CFormInput
                                //className="hst-input-link-cardlink"
                                type="text"
                                placeholder="www.example.com"
                                value={link}
                                onChange={(e) => setLink(e.target.value)}
                            />
                        </CInputGroup>
                        {/* Details */}
                        <CFormTextarea
                            label={`${t("label.details")}`}
                            rows={5}
                            className="mb-4"
                            value={details}
                            onChange={(e) => setDetails(e.target.value)}
                        />
                        {/* fullAddress */}
                        <CFormInput
                            label={`${t("label.address")}`}
                            className="mb-4"
                            value={fullAddress}
                            onChange={(e) => setFullAddress(e.target.value)}
                        />
                        <p className="my-4">{t('text.default_images')}</p>
                        <ImageCarousel 
                            onImageSelect={(base64, index) => 
                            handleDefaultImgClick(base64, index, setSelectedFile, setPreview, formData)
                        }/>
                        <CFormInput
                            type="file"
                            accept="image/*"
                            onChange={handleFileChange}
                        />
                        {preview && (
                            <div className="preview-card">
                                <img
                                    src={preview}
                                    alt={t('label.preview')}
                                    style={{ maxWidth: '200px', height: 'auto' }}
                                />
                            </div>
                        )}
                    </CForm>
                    <div
                        className="bg-transparent"
                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                    >
                        <Button
                            className="hst-btn-secondary mt-4"

                            onClick={() => navigate(-1)}
                        >
                            {t('text.go_back')}
                        </Button>
                        <Button
                            className="hst-btn-primary btn-primary mt-4"
                            onClick={handleUpdate}
                            disabled={isDisabled}
                            style={{fontWeight:"500"}}
                        >
                            {t('button.save')}
                        </Button>

                    </div>
                </div>
            </div>
        </div>
    );
}
